/**
 * Shared scope / date fields for dashboard widget edit modal.
 */
import type { WidgetScope } from '../../lib/widgetScope';
import { WIDGET_DATE_RANGE_OPTIONS, type WidgetDateRange } from '../../lib/widgetDateRange';
import { useWidgetCatalog } from '../../lib/useWidgetCatalog';

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  borderRadius: 6,
  border: '1px solid var(--header-border)',
  background: 'var(--bg)',
  color: 'var(--text)',
  fontSize: '0.875rem',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 4,
  fontSize: '0.875rem',
  color: 'var(--text-muted)',
};

interface WidgetScopeFieldsProps {
  scope: WidgetScope;
  onScopeChange: (s: WidgetScope) => void;
  campaignId: string;
  onCampaignIdChange: (id: string) => void;
  ipscapeAgentId: string;
  onAgentIdChange: (id: string) => void;
  dateRange: WidgetDateRange;
  onDateRangeChange: (r: WidgetDateRange) => void;
  showDateRange?: boolean;
}

export default function WidgetScopeFields({
  scope,
  onScopeChange,
  campaignId,
  onCampaignIdChange,
  ipscapeAgentId,
  onAgentIdChange,
  dateRange,
  onDateRangeChange,
  showDateRange = true,
}: WidgetScopeFieldsProps) {
  const { campaignOptions, agents, loading } = useWidgetCatalog();

  return (
    <>
      <div style={{ marginBottom: '1rem' }}>
        <label htmlFor="widget-scope" style={labelStyle}>
          Show data for
        </label>
        <select
          id="widget-scope"
          value={scope}
          onChange={(e) => {
            const s = e.target.value as WidgetScope;
            onScopeChange(s);
            if (s !== 'campaign') onCampaignIdChange('');
            if (s !== 'agent') onAgentIdChange('');
          }}
          style={selectStyle}
        >
          <option value="all">All (within your access)</option>
          <option value="campaign">Selected campaign</option>
          <option value="agent">Selected agent</option>
        </select>
      </div>

      {scope === 'campaign' && (
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="widget-campaign-select" style={labelStyle}>
            Campaign
          </label>
          <select
            id="widget-campaign-select"
            value={campaignId}
            onChange={(e) => onCampaignIdChange(e.target.value)}
            style={selectStyle}
            disabled={loading}
          >
            <option value="">— Select campaign —</option>
            {campaignOptions.map((c) =>
              c.campaign_id != null ? (
                <option key={c.campaign_id} value={String(c.campaign_id)}>
                  {c.title}
                </option>
              ) : null
            )}
          </select>
          {!loading && campaignOptions.length === 0 && (
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Sync campaigns in Settings → Campaigns first.
            </p>
          )}
        </div>
      )}

      {scope === 'agent' && (
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="widget-agent-select" style={labelStyle}>
            Agent
          </label>
          <select
            id="widget-agent-select"
            value={ipscapeAgentId}
            onChange={(e) => onAgentIdChange(e.target.value)}
            style={selectStyle}
            disabled={loading}
          >
            <option value="">— Select agent —</option>
            {agents.map((a) => (
              <option key={a.agent_id} value={String(a.agent_id)}>
                {a.display_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {showDateRange && (
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="widget-date-range" style={labelStyle}>
            Date range
          </label>
          <select
            id="widget-date-range"
            value={dateRange}
            onChange={(e) => onDateRangeChange(e.target.value as WidgetDateRange)}
            style={selectStyle}
          >
            {WIDGET_DATE_RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  );
}
